import { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Check, X, Trash2, MessageSquare, Filter } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700 border-green-300' },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700 border-red-300' }
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchReviews();
    fetchProducts();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/admin/reviews`;
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products?limit=100`);
      const productsMap = {};
      res.data.forEach(p => {
        productsMap[p.product_id] = p.name;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error(error);
    }
  };

  const updateStatus = async (reviewId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/reviews/${reviewId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === 'approved' ? 'Avis approuvé' : 'Avis rejeté');
      fetchReviews();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm('Supprimer cet avis définitivement ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avis supprimé');
      fetchReviews();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
      />
    ));
  };

  const pendingCount = reviews.filter(r => r.status === 'pending').length;

  return (
    <AdminLayout>
      <div data-testid="admin-reviews">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-brand-black flex items-center gap-3">
              <MessageSquare className="h-8 w-8 text-brand-gold" />
              Avis Clients
            </h1>
            <p className="text-brand-dark/60">
              {reviews.length} avis au total
              {pendingCount > 0 && (
                <span className="ml-2 text-orange-600 font-medium">
                  ({pendingCount} en attente)
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-brand-dark/50" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-brand-white border-brand-gold/20">
                <SelectValue placeholder="Filtrer" />
              </SelectTrigger>
              <SelectContent className="bg-brand-white border-brand-gold/20">
                <SelectItem value="all">Tous les avis</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="approved">Approuvés</SelectItem>
                <SelectItem value="rejected">Rejetés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 bg-brand-white rounded-lg border border-brand-gold/20">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-brand-dark/60">Aucun avis {filter !== 'all' && statusConfig[filter]?.label.toLowerCase()}</p>
          </div>
        ) : (
          <div className="bg-brand-white border border-brand-gold/20 rounded shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-brand-gold/20 bg-brand-cream">
                  <TableHead className="text-brand-dark/80">Produit</TableHead>
                  <TableHead className="text-brand-dark/80">Note</TableHead>
                  <TableHead className="text-brand-dark/80">Commentaire</TableHead>
                  <TableHead className="text-brand-dark/80">Auteur</TableHead>
                  <TableHead className="text-brand-dark/80">Date</TableHead>
                  <TableHead className="text-brand-dark/80">Statut</TableHead>
                  <TableHead className="text-brand-dark/80 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviews.map(review => (
                  <TableRow key={review.review_id} className="border-brand-gold/10 hover:bg-brand-cream">
                    <TableCell className="text-brand-black font-medium max-w-[150px] truncate">
                      {products[review.product_id] || review.product_id}
                    </TableCell>
                    <TableCell>
                      <div className="flex">{renderStars(review.rating)}</div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <p className="text-brand-dark/80 text-sm line-clamp-2">{review.comment}</p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-brand-black font-medium text-sm">{review.author_name}</p>
                        <p className="text-brand-dark/50 text-xs">{review.author_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-brand-dark/60 text-sm">
                      {new Date(review.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[review.status].color} border`}>
                        {statusConfig[review.status].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {review.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus(review.review_id, 'approved')}
                              className="text-green-600 hover:bg-green-50"
                              title="Approuver"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus(review.review_id, 'rejected')}
                              className="text-red-600 hover:bg-red-50"
                              title="Rejeter"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteReview(review.review_id)}
                          className="text-brand-dark/60 hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
